import axios from 'axios';
import type {
  Project, Folder, User, CreateProjectInput,
} from '@inno-ck/shared';
import { localFolderService, localProjectService } from './local-storage.service';

/**
 * API service — Imperative Shell
 *
 * In Next.js, all API routes are same-origin (no CORS, no VITE_API_URL needed).
 * Auth is managed by Auth.js v5 (next-auth) via session cookies.
 * Sign-in/sign-out use next-auth/react's signIn()/signOut() directly.
 *
 * Storage mode: the same service module powers two tiers —
 *  - "api"   → remote MongoDB via Next.js Route Handlers (default)
 *  - "local" → browser IndexedDB, no auth, no server (free tier, /free route)
 * The /free page bootstraps the mode before mount via setStorageMode('local').
 */

const api = axios.create({ baseURL: '', timeout: 8000 });

// ============================================================================
// Storage-mode dispatch (free tier uses local IndexedDB)
// ============================================================================

type StorageMode = 'api' | 'local';
let explicitMode: StorageMode | null = null;

/**
 * Resolves the active storage mode. Priority:
 *  1. Explicit mode set via setStorageMode() — used by FreeShell
 *  2. URL-based fallback: any path starting with /free → local
 *  3. Default: api
 */
function resolveMode(): StorageMode {
  if (explicitMode) return explicitMode;
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/free')) {
    return 'local';
  }
  return 'api';
}

export const getStorageMode = (): StorageMode => resolveMode();
export const setStorageMode = (mode: StorageMode): void => { explicitMode = mode; };
const isLocal = (): boolean => resolveMode() === 'local';

// ============================================================================
// Auth Service (custom routes only — signin/logout handled by Auth.js)
// ============================================================================

interface SignupResponse {
  success: boolean;
  message: string;
  user?: { user_id: string; email: string; user_confirmed: boolean };
  devVerificationCode?: string;
}

interface ConfirmResponse {
  success: boolean;
  message: string;
}

export const authService = {
  async register(data: { email: string; password: string; nickname?: string; phone_number?: string }): Promise<{ data: SignupResponse }> {
    return api.post('/api/auth/signup', data);
  },
  async confirmEmail(email: string, code: string): Promise<{ data: ConfirmResponse }> {
    return api.post('/api/auth/signup-confirm', { email, code });
  },
  async requestPasswordReset(email: string): Promise<{ data: { success: boolean; message: string } }> {
    return api.post('/api/auth/request-password-reset', { email });
  },
  async resetPassword(email: string, token: string, newPassword: string): Promise<{ data: { success: boolean; message: string } }> {
    return api.post('/api/auth/reset-password', { email, token, newPassword });
  },
};

// ============================================================================
// Folder Service
// ============================================================================

export const folderService = {
  async list(userId: string): Promise<Folder[]> {
    if (isLocal()) return localFolderService.list(userId);
    return (await api.get('/api/folders', { params: { userId } })).data;
  },
  async create(userId: string, name: string, parentId: string | null = null): Promise<Folder> {
    if (isLocal()) return localFolderService.create(userId, name, parentId);
    return (await api.post('/api/folders', { userId, name, parentId })).data;
  },
  async update(folderId: string, name: string): Promise<Folder> {
    if (isLocal()) return localFolderService.update(folderId, name);
    return (await api.put(`/api/folders/${folderId}`, { name })).data;
  },
  async remove(folderId: string): Promise<void> {
    if (isLocal()) { await localFolderService.remove(folderId); return; }
    return (await api.delete(`/api/folders/${folderId}`)).data;
  },
};

// ============================================================================
// Project Services
// ============================================================================

export const listProjects = async (userId: string): Promise<Project[]> => {
  if (isLocal()) return localProjectService.list(userId);
  return (await api.get('/api/projects/', { params: { userId } })).data;
};

export const getProject = async (id: string): Promise<Project> => {
  if (isLocal()) return localProjectService.get(id);
  return (await api.get(`/api/projects/${id}`)).data;
};

export const patchProject = async (id: string, data: Partial<Project>): Promise<Project> => {
  if (isLocal()) return localProjectService.patch(id, data);
  return (await api.patch(`/api/projects/${id}`, data)).data;
};

export const createProject = async (data: CreateProjectInput): Promise<Project> => {
  if (isLocal()) return localProjectService.upsertByName(data);
  return (await api.put('/api/projects', data)).data;
};

export const deleteProject = async (data: { projectName: string; userId: string }): Promise<void> => {
  if (isLocal()) { await localProjectService.deleteByName(data.userId, data.projectName); return; }
  return (await api.delete('/api/projects', { data })).data;
};

export const moveProject = async (projectId: string, folderId: string | null): Promise<Project> =>
  patchProject(projectId, { folderId, parentId: null } as unknown as Partial<Project>);

export const renameProject = async (projectId: string, projectName: string): Promise<Project> =>
  patchProject(projectId, { projectName } as unknown as Partial<Project>);

export const shareProject = async (projectId: string, targetUserId: string): Promise<void> =>
  (await api.post('/api/projects/share', { projectId, targetUserId })).data;

export const sendProjectFile = async (formData: FormData): Promise<void> =>
  (await api.post('/api/projects/send-file', formData, { 
    headers: { 'Content-Type': 'multipart/form-data' }, 
    timeout: 30000 
  })).data;

// ============================================================================
// User Services
// ============================================================================

export const getProfile = async (userId: string): Promise<User & { role?: string; isAdmin?: boolean }> =>
  (await api.get(`/api/users/${userId}`)).data;

export const updateProfile = async (userId: string, data: Partial<User>): Promise<User> =>
  (await api.patch(`/api/users/${userId}`, data)).data;

export const changePassword = async (oldPassword: string, newPassword: string): Promise<void> =>
  (await api.post('/api/auth/change-password', { oldPassword, newPassword })).data;

// ============================================================================
// Member Services
// ============================================================================

export const listMembers = async (): Promise<unknown[]> =>
  (await api.get('/api/members')).data;

export const inviteMember = async (data: { email: string; role: string }): Promise<unknown> =>
  (await api.post('/api/members/invite', data)).data;

export const updateMemberRole = async (uid: string, role: string): Promise<void> =>
  (await api.patch(`/api/members/${uid}`, { role })).data;

export const removeMember = async (uid: string): Promise<void> =>
  (await api.delete(`/api/members/${uid}`)).data;

// ============================================================================
// Admin Services
// ============================================================================

export const listOrgUsers = async (): Promise<unknown[]> =>
  (await api.get('/api/admin/users')).data;

export const updateUserAccess = async (uid: string, data: { active?: boolean; access?: string }): Promise<unknown> =>
  (await api.patch(`/api/admin/users/${uid}`, data)).data;

export const inviteUser = async (data: { email: string; role: string }): Promise<unknown> =>
  (await api.post('/api/admin/users/invite', data)).data;

export const removeUser = async (uid: string): Promise<void> =>
  (await api.delete(`/api/admin/users/${uid}`)).data;

// ============================================================================
// Notification Services
// ============================================================================

export const listNotifications = async (): Promise<unknown[]> =>
  (await api.get('/api/notifications')).data;

export const markNotifRead = async (id: string): Promise<void> =>
  (await api.patch(`/api/notifications/${id}`, { read: true })).data;

export const importSharedProject = async (projectId: string, userId: string): Promise<Project> => {
  const src = await getProject(projectId);
  return createProject({ 
    projectName: src.projectName, 
    userId, 
    jsonData: src.jsonData, 
    folderId: null, 
    parentId: null 
  } as CreateProjectInput);
};
